import { isBefore, isValid, parse } from 'date-fns';
import { COSTA_RICA } from 'src/constants/countryCode';
import { MAX_SHAREHOLDERS } from 'src/constants/shareholders';
import {
  isValidPersonDimex,
  isValidPersonId,
} from 'src/functions/identificationMethods';
import {
  NumberSchema,
  ObjectSchema,
  StringSchema,
  boolean,
  number,
  object,
  string,
} from 'yup';

export const PREFIX_KEY = 'beneficiary';

const nameErrorMessage = 'El nombre es requerido';
const lastNameErrorMessage = 'El apellido es requerido';
const genderErrorMessage = 'El género es obligatorio';
const birthdayErrorMessage = 'La fecha de nacimiento es obligatoria';
const idTypeErrorMessage = 'El tipo de identificación es requerido';
const participationErrorMessage = 'Porcentaje de participación requerido';
const participationMinErrorMessage =
  'Porcentaje incorrecto, debe ser mayor al 10%.';
const participationMaxErrorMessage =
  'Porcentaje incorrecto, el máximo es 100%.';
const emptyIdNumberError = 'El número de identificación es requerido';

export const errorMessages = {
  nameRequired: 'El nombre es obligatorio',
  nameMalFormed: 'El nombre solo puede contener letras',
  nameNoNumbers: 'El nombre no puede contener números',
  lastNameNoNumbers: 'El apellido no puede contener números',
  lastNameRequired: 'El apellido es obligatorio',
  lastNameMalFormed: 'El apellido solo puede contener letras',
  phoneRequired: 'El teléfono es obligatorio',
  phoneTooShort: 'El teléfono debe contener mínimo 8 caracteres',
  phoneMalformed: 'El teléfono solo puede contener números',
  phoneNotStartWithZero: 'El teléfono no puede iniciar con 0',
  phoneTooLong: 'El teléfono debe contener un máximo de 8 caracteres',
  cellPhoneRequired: 'El celular es obligatorio',
  cellPhoneTooShort: 'El celular debe contener mínimo 8 caracteres',
  cellPhoneMalformed: 'El celular solo puede contener números',
  homePhoneMalformed: 'El teléfono fijo solo puede contener números',
  cellPhoneNotStartWithZero: 'El celular no puede iniciar con 0',
  cellPhoneTooLong: 'El celular debe contener un máximo de 8 caracteres',
  emptyIdNumber: 'El número de identificación es requerido',
  emptyIdType: 'El tipo de identificación es requerido',
  addressDetailRequired: 'La dirección es requerida',
  addressDetailTooShort: 'La dirección debe contener al menos 10 caracteres',
  addressDetailTooLong: 'La dirección no debe superar los 250 caracteres',
  emptyOriginType: 'se debe completar el campo ¿De dónde provienen los fondos?',
  emptySalary: 'Se debe completar el campo Ingreso mensual',
  salaryMoreThanZero: 'El ingreso mensual debe ser mayor que 0',
  salaryNotDecimal: 'El ingreso mensual debe ser un número entero',
  emptyOriginCountry:
    'Se debe completar el campo País de domicilio de la empresa',
  dimexFormatErrorValidation: 'Debe iniciar con 1 y contener 12 caracteres',
};

type ShareholderRules = { [x: string]: StringSchema | NumberSchema };
const createShareValidations = (): ShareholderRules => {
  const helper = new Array(MAX_SHAREHOLDERS).fill(0);
  const validation: ShareholderRules = {};
  helper.forEach((_, index) => {
    validation[`share${index}firstName`] = string().when(
      ['totalShareHolder', 'hasShareholders'],
      {
        is: (totalShareHolder: number, hasShareholders: boolean) =>
          totalShareHolder > index && !hasShareholders,
        then: string().required(nameErrorMessage),
        otherwise: string(),
      },
    );
    validation[`share${index}lastName`] = string().when(
      ['totalShareHolder', 'hasShareholders'],
      {
        is: (totalShareHolder: number, hasShareholders: boolean) =>
          totalShareHolder > index && !hasShareholders,
        then: string().required(lastNameErrorMessage),
        otherwise: string(),
      },
    );
    validation[`share${index}gender`] = string().when(
      ['totalShareHolder', 'hasShareholders'],
      {
        is: (totalShareHolder: number, hasShareholders: boolean) =>
          totalShareHolder > index && !hasShareholders,
        then: string().required(genderErrorMessage),
        otherwise: string(),
      },
    );
    validation[`share${index}birthday`] = string().when(
      ['totalShareHolder', 'hasShareholders'],
      {
        is: (totalShareHolder: number, hasShareholders: boolean) =>
          totalShareHolder > index && !hasShareholders,
        then: string()
          .test(
            'Check format',
            'La fecha debe tener formato DD/MM/AAAA',
            (birthdayString: string): boolean => {
              const birthdate = parse(birthdayString, 'dd/MM/yyyy', new Date());
              return isValid(birthdate) && birthdayString.length === 10;
            },
          )
          .test(
            'Check date boundary',
            'La fecha no puede ser futura',
            (birthdayString: string): boolean => {
              const birthdate = parse(birthdayString, 'dd/MM/yyyy', new Date());
              return isBefore(birthdate, new Date());
            },
          )
          .required(birthdayErrorMessage),
        otherwise: string(),
      },
    );
    validation[`share${index}nationality`] = string().when(
      ['totalShareHolder', 'hasShareholders'],
      {
        is: (totalShareHolder: number, hasShareholders: boolean) =>
          totalShareHolder > index && !hasShareholders,
        then: string().required('La nacionalidad es obligatoria'),
        otherwise: string(),
      },
    );
    validation[`share${index}civilStatus`] = string().when(
      ['totalShareHolder', 'hasShareholders'],
      {
        is: (totalShareHolder: number, hasShareholders: boolean) =>
          totalShareHolder > index && !hasShareholders,
        then: string().required('El estado civil es obligatorio'),
        otherwise: string(),
      },
    );
    validation[`share${index}idType`] = string().when(
      ['totalShareHolder', 'hasShareholders'],
      {
        is: (totalShareHolder: number, hasShareholders: boolean) =>
          totalShareHolder > index && !hasShareholders,
        then: string().required(idTypeErrorMessage),
        otherwise: string(),
      },
    );
    validation[`share${index}id`] = string().when(
      ['totalShareHolder', 'hasShareholders'],
      {
        is: (totalShareHolder: number, hasShareholders: boolean) =>
          totalShareHolder > index && !hasShareholders,
        then: string()
          .when(`share${index}idType`, {
            is: 'N',
            then: string()
              .test(
                'Check id number format',
                'Formato de cédula inválida',
                isValidPersonId,
              )
              .required(emptyIdNumberError),
          })
          .when(`share${index}idType`, {
            is: 'U',
            then: string()
              .test(
                'Check DIMEX number format',
                errorMessages.dimexFormatErrorValidation,
                isValidPersonDimex,
              )
              .required(emptyIdNumberError)
              .max(12, errorMessages.dimexFormatErrorValidation),
          })
          .when(`share${index}idType`, {
            is: 'P',
            then: string().required(emptyIdNumberError),
          }),
        otherwise: string(),
      },
    );

    validation[`share${index}participationRate`] = number().when(
      ['totalShareHolder', 'hasShareholders'],
      {
        is: (totalShareHolder: number, hasShareholders: boolean) =>
          totalShareHolder > index && !hasShareholders,
        then: number()
          .typeError(participationErrorMessage)
          .min(10, participationMinErrorMessage)
          .max(100, participationMaxErrorMessage),
        otherwise: number()
          .transform((value: number) =>
            Number.isNaN(value) ? undefined : value,
          )
          .nullable(),
      },
    );
  });
  return validation;
};

type StockExchangeRules = { [x: string]: StringSchema | NumberSchema };
const createStockExchangeValidations = (): StockExchangeRules => {
  const validation: StockExchangeRules = {
    stockExchangeName: string().when(['isInStockExchange', 'hasShareholders'], {
      is: (isInStockExchange: boolean, hasShareholders: boolean) =>
        isInStockExchange && hasShareholders,
      then: string().required(
        'Se debe completar el campo Nombre de la bolsa de valores',
      ),
      otherwise: string().notRequired(),
    }),
    stockExchangeCountry: string().when(
      ['isInStockExchange', 'hasShareholders'],
      {
        is: (isInStockExchange: boolean, hasShareholders: boolean) =>
          isInStockExchange && hasShareholders,
        then: string().required(
          'Se debe completar el campo País de la bolsa de valores donde cotiza',
        ),
        otherwise: string().notRequired(),
      },
    ),
    companyType: string().when(['isInStockExchange', 'hasShareholders'], {
      is: (isInStockExchange: boolean, hasShareholders: boolean) =>
        !isInStockExchange && hasShareholders,
      then: string().required(
        'Se debe completar el campo ¿Qué tipo de empresa es?',
      ),
      otherwise: string().notRequired(),
    }),
  };

  return validation;
};

/**
 * Creates yup validation schema for the form fields
 * @param prefixKey prefix for the final beneficiary properties (needed in order to differentiate from
 * overlapping names with other properties)
 */
export default function makeValidationSchema(
  isIndividualProfile: boolean,
  insuranceValue: number,
): ObjectSchema {
  const commonValidations = {
    // Customer Properties
    idNumber: string()
      .required('Por favor incluya su número de RUC.')
      .min(10, 'El RUC debe contener un mínimo de 10 caracteres.')
      .max(10, 'El RUC debe contener un máximo de 10 caracteres.'),
    // Final Beneficiary Properties
    [`${PREFIX_KEY}FirstName`]: string()
      .matches(/^[A-Za-z\s]+$/, errorMessages.nameMalFormed)
      .matches(/^(\D*)$/, errorMessages.nameNoNumbers)
      .required(errorMessages.nameRequired),
    [`${PREFIX_KEY}LastName`]: string()
      .matches(/^[A-Za-z\s]+$/, errorMessages.lastNameMalFormed)
      .matches(/^(\D*)$/, errorMessages.lastNameNoNumbers)
      .required(errorMessages.lastNameRequired),
    [`${PREFIX_KEY}IdType`]: string().required(errorMessages.emptyIdType),
    [`${PREFIX_KEY}IdNumber`]: string()
      .when(`${PREFIX_KEY}IdType`, {
        is: 'N',
        then: string()
          .test(
            'Check id number format',
            'Formato de cédula inválida',
            isValidPersonId,
          )
          .required(errorMessages.emptyIdNumber),
      })
      .when(`${PREFIX_KEY}IdType`, {
        is: 'U',
        then: string()
          .test(
            'Check DIMEX number format',
            errorMessages.dimexFormatErrorValidation,
            isValidPersonDimex,
          )
          .required(errorMessages.emptyIdNumber)
          .max(12, errorMessages.dimexFormatErrorValidation),
      })
      .when(`${PREFIX_KEY}IdType`, {
        is: 'P',
        then: string().required(errorMessages.emptyIdNumber),
      }),
  };

  const shareholderValidations = createShareValidations();
  const stockExchangeValidations = createStockExchangeValidations();

  const individualValidations = {
    ...commonValidations,
    socialReason: string().required('La razón social es requerida'),
    fixNumber: string()
      .max(8, errorMessages.phoneTooLong)
      .matches(/^\d*$/, {
        message: errorMessages.phoneMalformed,
      })
      .test(
        'no-initial-zero',
        errorMessages.phoneNotStartWithZero,
        (value) => !value || value?.charAt(0) !== '0',
      ),
    notificationPreference: string().required(
      'La preferencia de notificación es requerida',
    ),
    legalEntityName: string().required('El nombre comercial es requerido'),
    birthday: string()
      .test(
        'Check format',
        'La fecha debe tener formato DD/MM/AAAA',
        (birthdayString: string): boolean => {
          const birthdate = parse(birthdayString, 'dd/MM/yyyy', new Date());
          return isValid(birthdate) && birthdayString.length === 10;
        },
      )
      .test(
        'Check date boundary',
        'La fecha no puede ser futura',
        (birthdayString: string): boolean => {
          const birthdate = parse(birthdayString, 'dd/MM/yyyy', new Date());
          return isBefore(birthdate, new Date());
        },
      )
      .required('La fecha de constitución es requerida'),
    phoneNumber: string()
      .required(errorMessages.cellPhoneRequired)
      .min(8, errorMessages.cellPhoneTooShort)
      .max(8, errorMessages.cellPhoneTooLong)
      .matches(/^\d*$/, {
        message: errorMessages.cellPhoneMalformed,
      })
      .test(
        'no-initial-zero',
        errorMessages.cellPhoneNotStartWithZero,
        (value) => !value || value?.charAt(0) !== '0',
      ),

    // address validations
    addressCountry: string().required('El país es requerido'),
    addressDetail: string()
      .min(10, errorMessages.addressDetailTooShort)
      .max(250, errorMessages.addressDetailTooLong)
      .required(errorMessages.addressDetailRequired),
    addressState: string().when('addressCountry', {
      is: COSTA_RICA,
      then: string().required('La provincia es requerida'),
      otherwise: string().notRequired(),
    }),
    addressCity: string().when('addressCountry', {
      is: COSTA_RICA,
      then: string().required('El cantón es requerido'),
      otherwise: string().notRequired(),
    }),
    addressTownship: string().when('addressCountry', {
      is: COSTA_RICA,
      then: string().required('El distrito es requerido'),
      otherwise: string().notRequired(),
    }),

    beneficiarySharePercentage: number().when('personExists', {
      is: false,
      then: number()
        .typeError('El campo solo acepta dígitos')
        .min(10, 'Porccentaje incorrecto, debe ser mayor al 10%.')
        .max(100, 'Porccentaje incorrecto, el máximo es 100%.')
        .required('Porcentaje de participación requerido'),
    }),
    [`${PREFIX_KEY}Phone1`]: string()
      .required(errorMessages.cellPhoneRequired)
      .min(8, errorMessages.cellPhoneTooShort)
      .max(8, errorMessages.cellPhoneTooLong)
      .matches(/^\d*$/, {
        message: errorMessages.cellPhoneMalformed,
      })
      .test(
        'no-initial-zero',
        errorMessages.cellPhoneNotStartWithZero,
        (value) => !value || value?.charAt(0) !== '0',
      ),
    [`${PREFIX_KEY}Birthdate`]: string()
      .test(
        'Check format',
        'La fecha debe tener formato DD/MM/AAAA',
        (birthdayString: string): boolean => {
          const birthdate = parse(birthdayString, 'dd/MM/yyyy', new Date());
          return isValid(birthdate) && birthdayString.length === 10;
        },
      )
      .test(
        'Check date boundary',
        'La fecha no puede ser futura',
        (birthdayString: string): boolean => {
          const birthdate = parse(birthdayString, 'dd/MM/yyyy', new Date());
          return isBefore(birthdate, new Date());
        },
      )
      .required('La fecha de nacimiento es requerida'),
    [`${PREFIX_KEY}BirthPlaceId`]: string().required(
      'El país de nacimiento es requerido',
    ),
    [`${PREFIX_KEY}Occupation`]: string().required('La profesión es requerida'),
    [`${PREFIX_KEY}CompanyRole`]: string().required(
      'El cargo dentro de la empresa es requerido',
    ),
    // address validations
    [`${PREFIX_KEY}CountryId`]: string().required('El país es requerido'),
    [`${PREFIX_KEY}AddressDetail`]: string()
      .min(10, errorMessages.addressDetailTooShort)
      .max(250, errorMessages.addressDetailTooLong)
      .required(errorMessages.addressDetailRequired),
    [`${PREFIX_KEY}StateId`]: string().when([`${PREFIX_KEY}CountryId`], {
      is: COSTA_RICA,
      then: string().required('La provincia es requerida'),
      otherwise: string().notRequired(),
    }),
    [`${PREFIX_KEY}CityId`]: string().when([`${PREFIX_KEY}CountryId`], {
      is: COSTA_RICA,
      then: string().required('El cantón es requerido'),
      otherwise: string().notRequired(),
    }),
    [`${PREFIX_KEY}TownshipId`]: string().when([`${PREFIX_KEY}CountryId`], {
      is: COSTA_RICA,
      then: string().required('El distrito es requerido'),
      otherwise: string().notRequired(),
    }),
    // data
    email: string()
      .required('El correo es requerido')
      .email('Formato de correo inválido'),

    // Origin Fund validations
    originType: string().required(errorMessages.emptyOriginType),
    salary: number()
      .typeError(errorMessages.emptySalary)
      .required(errorMessages.emptySalary)
      .moreThan(0, errorMessages.salaryMoreThanZero)
      .integer(errorMessages.salaryNotDecimal),
    originCountry: string().required(errorMessages.emptyOriginCountry),
    pep: boolean().required('Favor especificar si es persona pep'),
    bloodRelatedToPep: boolean().required(
      'Favor especificar si es persona pep',
    ),
    pepLawArticle: boolean().required('Favor especificar si es persona pep'),
    currentBank: string(),
    creditAmount: number()
      .transform((value) => (Number.isNaN(value) ? undefined : value))
      .max(
        insuranceValue,
        'El monto de acreencia no puede ser mayor al monto asegurado',
      )
      .when(['currentBank'], {
        is: (currentBank) => currentBank !== '',
        then: number()
          .required('Monto de Acreencia requerido')
          .typeError('El valor debe ser numérico'),
        otherwise: number()
          .transform((value) => (Number.isNaN(value) ? undefined : value))
          .notRequired(),
      }),
    birthPlace: string().required('El país de constitución es requerido'),
    economicActivity: string().required('La actividad económica es requerida'),

    // shareholder
    totalShareHolder: number(),
    // hasShareholders
    hasShareholders: boolean(),

    // enterprise stock exchange data
    isInStockExchange: boolean(),
    stockExchangeName: string(),
    stockExchangeCountry: string(),

    ...shareholderValidations,
    ...stockExchangeValidations,
  };

  const collectiveValidations = {
    ...commonValidations,
    effectiveDate: string().required(
      'La fecha de inicio de protección de tu seguro es requerida',
    ),
    legalEntityName: string().required('El nombre de empresa es obligatorio'),
    phoneNumber: string()
      .required(errorMessages.phoneRequired)
      .min(8, errorMessages.phoneTooShort)
      .max(8, errorMessages.phoneTooLong)
      .matches(/^\d*$/, {
        message: errorMessages.phoneMalformed,
      })
      .test(
        'no-initial-zero',
        errorMessages.phoneNotStartWithZero,
        (value) => !value || value?.charAt(0) !== '0',
      ),
    email: string()
      .required('El correo es requerido')
      .email('Formato de correo inválido'),
    legalEntityOccupation: string().required(
      'La actividad económica de la empresa es requerida',
    ),
  };

  return isIndividualProfile
    ? object().shape(individualValidations)
    : object().shape(collectiveValidations);
}
